import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, Smartphone, Wifi, Clock, ExternalLink } from "lucide-react";

const SuccessModal = ({ open, onClose, redeemData }) => {
  if (!redeemData) return null;

  const getIcon = () => {
    return redeemData.conversionType === "data" ? (
      <Wifi className="w-8 h-8 text-emerald-500" />
    ) : (
      <Smartphone className="w-8 h-8 text-emerald-500" />
    );
  };

  const getSuccessMessage = () => {
    return redeemData.conversionType === "data" 
      ? "Data bundle redemption successful!"
      : "Airtime redemption successful!";
  };

  const getDeliveryMessage = () => {
    return redeemData.conversionType === "data"
      ? "Your data bundle will be delivered to your registered phone number within 5 minutes."
      : "Airtime has been credited to your registered phone number and should be available within 5 minutes.";
  };

  // Generate a mock transaction ID for demo purposes
  const transactionId = `TS${Date.now().toString().slice(-8)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-4xl bg-white rounded-2xl ring-1 ring-gray-100 shadow-2xl max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500" />

            {/* Fixed Header */}
            <div className="flex flex-shrink-0 justify-between items-center p-6 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">Redemption Complete</h3>
              <button
                onClick={onClose}
                className="grid place-items-center w-10 h-10 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1">
              <div className="p-6">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Success Animation */}
                    <div className="text-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                        className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-emerald-100 rounded-full"
                      >
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      </motion.div>
                      
                      <motion.h4
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mb-2 text-lg font-semibold text-gray-900"
                      >
                        {getSuccessMessage()}
                      </motion.h4>
                      
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-sm text-gray-600"
                      >
                        {getDeliveryMessage()}
                      </motion.p>
                    </div>

                    {/* Transaction Details */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="p-4 bg-emerald-50 rounded-xl"
                    >
                      <h5 className="mb-3 font-medium text-emerald-900">💳 Transaction Summary</h5>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Transaction ID:</span>
                          <span className="font-mono text-sm font-semibold text-gray-900">{transactionId}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Points redeemed:</span>
                          <span className="font-semibold text-gray-900">-{redeemData.points.toLocaleString()} points</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Received:</span>
                          <div className="flex gap-2 items-center">
                            {getIcon()}
                            <span className="font-semibold text-emerald-600">{redeemData.conversionValue}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Date & Time:</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Status Update */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="flex gap-3 items-center p-4 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Clock className="flex-shrink-0 w-5 h-5 text-blue-600" />
                      </motion.div>
                      <div>
                        <p className="mb-1 text-sm font-medium text-blue-800">⏳ Processing...</p>
                        <p className="text-xs text-blue-700">
                          Your {redeemData.conversionTypeLabel.toLowerCase()} is being processed and will be delivered shortly.
                        </p>
                      </div>
                    </motion.div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Next Steps */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="p-4 bg-yellow-50 rounded-xl border border-yellow-200"
                    >
                      <h5 className="mb-3 font-medium text-yellow-900">📋 What's Next?</h5>
                      <ul className="space-y-2 text-xs text-yellow-800">
                        <li>• Check your phone for an SMS confirmation</li>
                        <li>• {redeemData.conversionType === "data" ? "Data bundle will auto-activate on your line" : "Airtime balance will update immediately"}</li>
                        <li>• Keep this transaction ID for your records</li>
                        <li>• Contact us if not received within 10 minutes</li>
                      </ul>
                    </motion.div>

                    {/* Helpful Tips */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      className="p-4 bg-purple-50 rounded-xl"
                    >
                      <h5 className="mb-3 font-medium text-purple-900">💡 Helpful Tips</h5>
                      <ul className="space-y-2 text-xs text-purple-700">
                        <li>• Check your phone balance to confirm receipt</li>
                        <li>• {redeemData.conversionType === "data" ? "Data bundles expire in 30 days" : "Airtime never expires"}</li>
                        <li>• Earn more points by purchasing TripSecure policies</li>
                        <li>• Share your referral code to earn bonus points</li>
                      </ul>
                    </motion.div>

                    {/* Support Information */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 }}
                      className="p-4 bg-gray-50 rounded-xl border border-gray-200"
                    >
                      <h5 className="mb-3 font-medium text-gray-900">🤝 Need Help?</h5>
                      <div className="space-y-2 text-xs text-gray-600">
                        <p>If you experience any issues with your redemption:</p>
                        <div className="flex flex-col space-y-1">
                          <button className="text-left text-emerald-600 underline hover:text-emerald-700">
                            📞 Call Support: +233 123 456 789
                          </button>
                          <button className="text-left text-emerald-600 underline hover:text-emerald-700">
                            💬 Live Chat Support
                          </button>
                          <button className="text-left text-emerald-600 underline hover:text-emerald-700">
                            📧 Email: support@tripsecure.com
                          </button>
                        </div>
                      </div>
                    </motion.div>

                    {/* Rate Experience */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.0 }}
                      className="p-4 bg-emerald-50 rounded-xl border border-emerald-200"
                    >
                      <h5 className="mb-2 font-medium text-emerald-900">⭐ Rate Your Experience</h5>
                      <p className="text-xs text-emerald-700 mb-3">How was your redemption experience?</p>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            className="text-yellow-400 hover:text-yellow-500 transition-colors"
                            onClick={() => console.log(`Rated ${star} stars`)}
                          >
                            ⭐
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Actions at Bottom */}
            <div className="flex flex-shrink-0 gap-3 p-6 border-t border-gray-100">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 font-medium text-white bg-emerald-500 rounded-lg transition-colors hover:bg-emerald-600"
              >
                Continue to Dashboard
              </button>
              
              <button
                onClick={() => {
                  // Handle view history functionality
                  console.log("View redemption history");
                }}
                className="flex flex-1 gap-2 justify-center items-center px-6 py-3 font-medium text-emerald-600 bg-emerald-50 rounded-lg transition-colors hover:bg-emerald-100"
              >
                <span>View History</span>
                <ExternalLink size={16} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SuccessModal;
